import React, { CSSProperties, useEffect, useMemo, useState } from "react";

import { Button, Container, Grid, Icon, Input } from "semantic-ui-react";

import moment from "moment";

import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

import SnackMessage from "react-lib/apps/common/SnackMessage";

import ModInfoOA from "../IsHealth/REG/ModInfoOA";

import ModHolidayInfo from "./ModHolidayInfo";

import {
  ACTIONS,
  BTN_ACTS,
  HolidayInfoType,
  PickedState,
  RunSequence,
  SEQUENCE_NAME,
  SetProp,
  State,
} from "./sequence/HolidaySetting";

import { beStringToDateObject } from "react-lib/utils/dateUtils";

type CardHolidaySettingProps = Pick<PickedState, "buttonLoadCheck" | "errorMessage"> & {
  setProp: SetProp;
  runSequence: RunSequence;
  HolidaySettingSequence: State["HolidaySettingSequence"];
};

type Styles = Record<
  | "addButton"
  | "controlsWrapper"
  | "copyButton"
  | "emptyState"
  | "holidayButton"
  | "holidayList"
  | "monthCard"
  | "monthHeader"
  | "title"
  | "yearControlWrapper",
  CSSProperties
>;

const styles: Styles = {
  addButton: {
    background: "var(--primary-theme-color)",
    borderRadius: "6px",
    color: "white",
    padding: "12px 24px",
  },
  controlsWrapper: {
    alignItems: "center",
    display: "flex",
    flexWrap: "wrap",
    gap: "16px",
    marginBottom: "2rem",
  },
  copyButton: {
    background: "#B3B3B3",
    borderRadius: "6px",
    color: "white",
    padding: "12px 24px",
  },
  emptyState: {
    color: "#999",
    padding: "3.5rem 12px",
    textAlign: "center",
  },
  holidayButton: {
    alignItems: "center",
    border: "1px solid rgba(217, 217, 217, 0.5)",
    borderRadius: "6px",
    boxShadow: "none",
    display: "grid",
    gridTemplateColumns: "40% 1fr",
    marginBottom: "8px",
    padding: "7px 12px",
    textAlign: "left",
    width: "100%",
  },
  holidayList: {
    flex: 1,
    overflowX: "hidden",
    overflowY: "auto",
    padding: "8px",
  },
  monthCard: {
    border: "1px solid #D9D9D9",
    display: "flex",
    flexDirection: "column",
    height: "200px",
    overflow: "hidden",
  },
  monthHeader: {
    background: "#FFD5D5",
    fontWeight: "bold",
    margin: "0.5rem 0.5rem 0",
    padding: "4px 12px",
  },
  title: {
    color: "#3C3C3C",
    fontSize: "1.35rem",
    fontWeight: "bold",
    marginBottom: "24px",
  },
  yearControlWrapper: {
    alignItems: "center",
    display: "flex",
    gap: "8px",
  },
};

const CardHolidaySetting = (props: CardHolidaySettingProps) => {
  const [year, setYear] = useState(moment().year());
  const [searchText, setSearchText] = useState("");
  const [modHoliday, setModHoliday] = useState<Partial<HolidayInfoType> | null>(null);
  const [modInfoMsg, setModInfoMsg] = useState("");

  useEffect(() => {
    props.runSequence({ sequence: "HolidaySetting", restart: true });
  }, []);

  const formattedHoliday = useMemo(() => {
    const items = props.HolidaySettingSequence?.holiday?.items ?? [];

    const monthNames = [
      "มกราคม",
      "กุมภาพันธ์",
      "มีนาคม",
      "เมษายน",
      "พฤษภาคม",
      "มิถุนายน",
      "กรกฎาคม",
      "สิงหาคม",
      "กันยายน",
      "ตุลาคม",
      "พฤศจิกายน",
      "ธันวาคม",
    ];

    return monthNames.map((name, index) => {
      const filtered = items.flatMap((item) => {
        const date = item.dates[0];
        const isMatched = moment(date).isSame(moment(`${year}-${index + 1}-01`), "months");

        return isMatched ? [{ ...item, day: moment(date).format("DD") }] : [];
      });

      const sorted = [...filtered].sort((a, b) => moment(a.dates[0]).diff(moment(b.dates[0])));

      return {
        holidays: sorted,
        month: name,
      };
    });
  }, [props.HolidaySettingSequence?.holiday, year]);

  const filteredHoliday = useMemo(() => {
    if (searchText) {
      return formattedHoliday
        .map((item) => ({
          ...item,
          holidays: item.holidays.filter((holiday) =>
            holiday.detail.toLowerCase().includes(searchText.toLowerCase())
          ),
        }))
        .filter((item) => item.holidays.length > 0);
    }

    return formattedHoliday;
  }, [formattedHoliday, searchText]);

  const currentHolidayList = useMemo(() => {
    const items = props.HolidaySettingSequence?.holiday?.items ?? [];

    return items.map((item: any) => beStringToDateObject(item.date?.[0]));
  }, [props.HolidaySettingSequence?.holiday]);

  const handlePreviousYear = () => {
    setYear(year - 1);
  };

  const handleNextYear = () => {
    setYear(year + 1);
  };

  const handleCopy = () => {
    props.runSequence({
      sequence: "HolidaySetting",
      action: ACTIONS.COPY_YEAR,
      year,
      onError: () => {
        setModInfoMsg("เกิดข้อผิดพลาด");
      },
      onSuccess: () => {
        setModInfoMsg("บันทึกสำเร็จ");
      },
    });
  };

  return (
    <>
      <SnackMessage
        error={props.errorMessage?.[SEQUENCE_NAME]}
        success={null}
        onClose={() => {
          props.setProp(`errorMessage.${SEQUENCE_NAME}`, null);
        }}
      />

      <Container style={{ height: "calc(100vh - 40px)", overflow: "auto", padding: "24px" }} fluid>
        <div style={styles.title}>ตั้งค่าวันหยุดทำการ</div>

        <div style={styles.controlsWrapper}>
          <div style={styles.yearControlWrapper}>
            <Icon name="chevron left" size="large" link onClick={handlePreviousYear} />
            <Button style={{ background: "rgba(255, 229, 229, 0.51)", borderRadius: "5px" }}>
              {year}
            </Button>
            <Icon name="chevron right" size="large" link onClick={handleNextYear} />
          </div>

          <Input
            style={{ minWidth: "35%" }}
            icon="search"
            input={{ style: { border: "1px solid #D9D9D9", borderRadius: "7px" } }}
            placeholder="ค้นหาด้วยชื่อวันหยุด"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          <div style={{ flex: 1 }} />

          <Button
            style={styles.addButton}
            onClick={() => {
              setModHoliday({});
            }}
          >
            <Icon name="plus" />
            เพิ่มวันหยุด
          </Button>

          {moment().year() === year && (
            <ButtonLoadCheck
              setProp={props.setProp}
              style={styles.copyButton}
              icon="copy outline"
              paramKey={BTN_ACTS.COPY_YEAR}
              size="medium"
              title="คัดลอกวันหยุดไปปีหน้า"
              buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.COPY_YEAR]}
              onClick={handleCopy}
            />
          )}
        </div>

        <Grid columns={4} stackable>
          {filteredHoliday.map((item, index) => (
            <Grid.Column key={index} style={{ padding: 0 }}>
              <div style={styles.monthCard}>
                <div style={styles.monthHeader}>{item.month}</div>

                <div style={styles.holidayList}>
                  {item.holidays.length > 0 ? (
                    item.holidays.map((holiday, hIndex) => (
                      <Button
                        key={hIndex}
                        style={styles.holidayButton}
                        basic
                        onClick={() => {
                          setModHoliday(holiday);
                        }}
                      >
                        <span style={{ color: "rgba(0, 0, 0, 1)" }}> {holiday.day} </span>
                        <span style={{ color: "rgba(0, 0, 0, 1)" }}> {holiday.detail} </span>
                      </Button>
                    ))
                  ) : (
                    <div style={styles.emptyState}>ไม่มีวันหยุด</div>
                  )}
                </div>
              </div>
            </Grid.Column>
          ))}
        </Grid>
      </Container>

      <ModHolidayInfo
        setProp={props.setProp}
        runSequence={props.runSequence}
        data={modHoliday}
        holidays={currentHolidayList}
        open={!!modHoliday}
        year={year}
        buttonLoadCheck={props.buttonLoadCheck}
        onClose={() => {
          setModHoliday(null);
        }}
      />

      <ModInfoOA
        btnStyle={{ backgroundColor: "#b8b8b8" }}
        btnText="ปิดหน้าจอ"
        open={!!modInfoMsg}
        type={modInfoMsg.includes("บันทึกสำเร็จ") ? "success" : "error"}
        onApprove={() => {
          setModInfoMsg("");
        }}
        onClose={() => {
          setModInfoMsg("");
        }}
        subtitle={
          <div>
            <label style={{ color: "#000000" }}>คัดลอกวันหยุดไปปีหน้า</label>{" "}
            <label style={{ paddingLeft: "5px" }}>สำเร็จ</label>
          </div>
        }
      />
    </>
  );
};

export default React.memo(CardHolidaySetting);
